import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Upload, X } from "lucide-react";
import { toast } from "sonner";
import { http, imgUrl, errMsg } from "@/lib/api";

const cls = "border-[#163D43] bg-[#041214] text-teal-50";

const ImageField = ({ value, onChange, k }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await http.post("/admin/upload", fd);
      onChange(data.url);
      toast.success("Imagem enviada");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-3">
      {value && <img src={imgUrl(value)} alt="" className="h-14 w-14 rounded object-cover" />}
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="URL da imagem" className={cls} data-testid={`field-${k}`} />
      <label className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-[#2DD4BF]/50 px-3 py-2 text-xs text-[#2DD4BF]" data-testid={`field-${k}-upload`}>
        <Upload className="h-3.5 w-3.5" /> {busy ? "..." : "Enviar"}
        <input type="file" accept="image/*" className="hidden" onChange={upload} />
      </label>
    </div>
  );
};

const GalleryField = ({ value, onChange, k, max }) => {
  const [busy, setBusy] = useState(false);
  const full = max && value.length >= max;
  const upload = async (e) => {
    let files = [...(e.target.files || [])];
    if (!files.length) return;
    if (max && value.length + files.length > max) {
      toast.error(`Máximo de ${max} imagens`);
      files = files.slice(0, max - value.length);
    }
    setBusy(true);
    const urls = [];
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await http.post("/admin/upload", fd);
        urls.push(data.url);
      } catch (err) {
        toast.error(errMsg(err));
      }
    }
    onChange([...value, ...urls]);
    setBusy(false);
  };
  return (
    <div className="space-y-2" data-testid={`field-${k}`}>
      <div className="grid grid-cols-4 gap-2">
        {value.map((u, i) => (
          <div key={u + i} className="relative">
            <img src={imgUrl(u)} alt="" className="aspect-square w-full rounded object-cover" />
            <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="absolute right-1 top-1 rounded-full bg-[#041214]/90 p-1 text-red-300" data-testid={`field-${k}-remove-${i}`} aria-label="Remover"><X className="h-3 w-3" /></button>
          </div>
        ))}
      </div>
      {!full && (
        <label className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-[#2DD4BF]/50 px-3 py-2 text-xs text-[#2DD4BF]" data-testid={`field-${k}-upload`}>
          <Upload className="h-3.5 w-3.5" /> {busy ? "Enviando..." : `Adicionar prévias${max ? ` (${value.length}/${max})` : ""}`}
          <input type="file" accept="image/*" multiple className="hidden" onChange={upload} />
        </label>
      )}
      {full && <p className="text-xs text-teal-300">Limite de {max} imagens atingido.</p>}
    </div>
  );
};

export const FieldInput = ({ f, value, onChange }) => {
  if (f.type === "gallery") return <GalleryField value={value || []} onChange={onChange} k={f.key} max={f.max} />;
  if (f.type === "textarea") return <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={`field-${f.key}`} />;
  if (f.type === "lines")
    return <Textarea value={(value || []).join("\n")} onChange={(e) => onChange(e.target.value.split("\n"))} placeholder="Um item por linha" className={cls} data-testid={`field-${f.key}`} />;
  if (f.type === "bool") return <Switch checked={!!value} onCheckedChange={onChange} data-testid={`field-${f.key}`} />;
  if (f.type === "image") return <ImageField value={value} onChange={onChange} k={f.key} />;
  if (f.type === "select")
    return (
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={`h-9 w-full rounded-md border px-3 text-sm ${cls}`} data-testid={`field-${f.key}`}>
        {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    );
  if (f.type === "number") return <Input type="number" step="any" value={value ?? 0} onChange={(e) => onChange(Number(e.target.value))} className={cls} data-testid={`field-${f.key}`} />;
  if (f.type === "secret") return <Input type="password" autoComplete="off" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder} className={cls} data-testid={`field-${f.key}`} />;
  return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder} className={cls} data-testid={`field-${f.key}`} />;
};
